/**
 * Results of a Goethe-Zertifikat sitting (D-118), on the official scale of its level: points of
 * each module (A1 x 1.66, A2 x 1.25, B1 to C1 the table of 30 items, C2 weighted parts), the
 * rating (« sehr gut » … « nicht bestanden »), the pass (A1 / A2: 60 points in all — A2: 45 at
 * the written part and 15 at the oral; B1 to C2: 60 per module), then the full correction: every
 * item with the right answer and its explanation (French or English, the « How to succeed? »
 * link), every Hören document to hear again with its transcript, every writing and speaking part
 * with its two raters, criteria, errors and model answer. Always an estimate.
 */

import { MarkNotes } from "../MarkNotes";
import { RecordingExpired } from "../RecordingExpired";
import { useAcademyAttemptsRetrieve, useAcademyAttemptsReviewRetrieve } from "@agencyos/api-client";
import {
  GOETHE_MODULES,
  type GoetheModuleResult,
  type GoethePassage,
  type GoetheSkill,
  formatPoints,
  parseGoethePassage,
  parseGoetheResults,
  parsePicture,
  parsePictures,
  parseSpokenLines,
} from "@agencyos/core";
import { Badge, GlassCard, Skeleton, buttonClass } from "@agencyos/ui-web";
import { Award, CheckCircle2, Clock, XCircle } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ExamPictureView } from "../ExamPicture";
import { MethodLink } from "../ielts/MethodLink";
import { AudioReplay, SpeechReplay, Transcript } from "../ListeningReplay";
import { isObject } from "../types";
import { CardTitle, PageHead, SkillIcon } from "../ui";
import { unavailableSkills, useAcademyPaths } from "../paths";
import { GoetheExampleCard, GoetheItem, GoethePool, GoetheTexts } from "./GoetheItems";
import "./goethe.css";
import { FeedbackInMyLanguage, InMyLanguage } from "../ielts/InMyLanguage";

function ratingText(rating: GoetheModuleResult["rating"], language: string): string {
  if (!rating) return "";
  const local = language.startsWith("en") ? rating.en : rating.fr;
  return local && local !== rating.de ? `${rating.de} (${local})` : rating.de;
}

function ModuleCard({ result, unavailable = false }: { result: GoetheModuleResult; unavailable?: boolean }) {
  const { t, i18n } = useTranslation();
  const skill = result.skill;
  return (
    <li className={`ac-goethe-result ac-surface ac-skill--${skill}`}>
      <span className="ac-goethe-result__head">
        <SkillIcon skill={skill} size={16} />
        <strong>{GOETHE_MODULES[skill]}</strong>
        <span className="ac-caption">{t(`goethe.module.${skill}`)}</span>
      </span>
      {unavailable ? (
        <span className="ac-caption">{t("freeMock.delayedShort")}</span>
      ) : result.pending ? (
        <span className="ac-caption">
          <Clock aria-hidden size={13} /> {t("goethe.results.pending")}
        </span>
      ) : (
        <>
          <span className="ac-goethe-result__points ao-num">
            {formatPoints(result.points, i18n.language)} <span className="ac-caption">/ {formatPoints(result.max, i18n.language)}</span>
          </span>
          {result.right !== null && result.items !== null && <span className="ac-caption ao-num">{t("goethe.results.right", { n: result.right, total: result.items })}</span>}
          {result.passed !== null && (
            <Badge tone={result.passed ? "success" : "danger"}>{result.passed ? t("goethe.results.passed") : t("goethe.results.failed")}</Badge>
          )}
          {result.rating && <span className="ac-goethe-result__rating">{ratingText(result.rating, i18n.language)}</span>}
          {result.thirdRating && <p className="ac-footnote">{t("goethe.results.thirdRating")}</p>}
          {result.eRule && <p className="ac-footnote ko">{t("goethe.results.eRule")}</p>}
          {Object.keys(result.teile).length > 0 && (
            <ul className="ac-goethe-teile">
              {Object.entries(result.teile).map(([teil, row]) => (
                <li key={teil} className="ao-num">
                  {t("goethe.player.teil", { n: teil })}: {row.items !== undefined ? `${row.right ?? 0}/${row.items}` : formatPoints(row.points ?? 0, i18n.language)}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </li>
  );
}

function asList(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value) ? value.filter(isObject) : [];
}

function text(value: unknown): string {
  return typeof value === "string" ? value : typeof value === "number" ? String(value) : "";
}

function ProductionReview({ row }: { row: Record<string, unknown> }) {
  const { t, i18n } = useTranslation();
  const grade = isObject(row.grade) ? row.grade : {};
  const models = isObject(row.models) ? row.models : {};
  const explanation = isObject(models.explanation) ? models.explanation : {};
  const help = text(i18n.language.startsWith("en") ? explanation.en : explanation.fr) || text(explanation.fr);
  const criteria = asList(grade.criteria);
  const items = asList(grade.items);
  const raters = Array.isArray(grade.rater_totals) ? (grade.rater_totals as unknown[]).map(Number) : [];
  const skill = text(row.skill) as GoetheSkill;
  const model = text(models.model_answer) || text(models.model_dialogue);
  return (
    <GlassCard as="article" className="ac-card ac-goethe-production">
      <CardTitle icon={<SkillIcon skill={skill} size={16} />}>
        {GOETHE_MODULES[skill]} · {t("goethe.player.teil", { n: text(row.teil) })}
        {typeof grade.points === "number" && (
          <span className="ao-num">
            {" "}
            · {formatPoints(Number(grade.points), i18n.language)} / {formatPoints(Number(row.points_max), i18n.language)}
          </span>
        )}
      </CardTitle>
      <p className="ac-prompt" lang="de">
        {text(row.prompt)}
      </p>
      {text(row.text) && (
        <p className="ac-goethe-copy" lang="de">
          {text(row.text)}
        </p>
      )}
      {asList(row.turns).length > 0 && (
        <ul className="ac-transcript">
          {asList(row.turns)
            .filter((turn) => text(turn.text))
            .map((turn, index) => (
              <li key={index} className={`ac-bubble ac-bubble--${turn.role === "learner" ? "me" : "them"}`}>
                <strong className="ac-bubble__who">{t(`goethe.speaking.who.${turn.role === "learner" ? "learner" : turn.role === "partner" ? "partner" : "examiner"}`)}</strong> <span lang="de">{text(turn.text)}</span>
              </li>
            ))}
        </ul>
      )}
      {typeof row.audio_url === "string" && row.audio_url && <AudioReplay url={row.audio_url} label={t("goethe.review.myRecording")} />}
      {row.audio_expired === true && <RecordingExpired />}
      {grade.unanswered === true && <p className="ac-status ko">{t("academy.results.unanswered")}</p>}
      <MarkNotes grade={row.grade} productionId={typeof row.id === "string" ? row.id : undefined} />
      {row.status !== "graded" && <p className="ac-note">{row.status === "to_trainer" ? t("goethe.results.toTrainer") : t("goethe.results.pending")}</p>}
      {raters.length === 2 && (
        <p className="ac-caption ao-num">
          {t("goethe.review.raters", { a: formatPoints(raters[0] ?? 0, i18n.language), b: formatPoints(raters[1] ?? 0, i18n.language) })}
          {grade.e_rule ? ` · ${t("goethe.results.eRule")}` : ""}
        </p>
      )}
      {typeof grade.word_warning === "string" && <p className="ko ac-footnote">{grade.word_warning}</p>}
      {items.length > 0 && (
        <ol className="ac-goethe-review-items" lang="de">
          {items.map((item) => (
            <li key={text(item.n)}>
              {item.correct || Number(item.points) > 0 ? <CheckCircle2 aria-hidden size={14} className="ok" /> : <XCircle aria-hidden size={14} className="ko" />} <strong>{text(item.n)}</strong> {text(item.response) || "—"}
              <span className="ac-caption"> → {Array.isArray(item.accepted) ? (item.accepted as unknown[]).map(text).join(" / ") : ""}</span>
            </li>
          ))}
        </ol>
      )}
      {criteria.length > 0 && (
        <table className="ac-goethe-criteria">
          <thead>
            <tr>
              <th scope="col">{t("goethe.review.criterion")}</th>
              <th scope="col">{t("goethe.review.points")}</th>
            </tr>
          </thead>
          <tbody>
            {criteria.map((c) => {
              const label = isObject(c.label) ? c.label : {};
              return (
                <tr key={text(c.key)}>
                  <td>{text(i18n.language.startsWith("en") ? label.en : label.fr) || text(c.key)}</td>
                  <td className="ao-num">
                    {formatPoints(Number(c.points), i18n.language)} / {formatPoints(Number(c.max), i18n.language)} {text(c.letter) && <Badge>{text(c.letter)}</Badge>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      <FeedbackInMyLanguage
        comment={text(grade.comment)}
        errors={asList(grade.errors).map((e) => ({ excerpt: text(e.excerpt), correction: text(e.correction), explanation: text(e.explanation) }))}
        examLanguage="de"
      />
      {asList(grade.errors).length > 0 && (
        <ul className="ac-goethe-errors">
          {asList(grade.errors).map((error, index) => (
            <li key={index}>
              <del lang="de">{text(error.excerpt)}</del> → <ins lang="de">{text(error.correction)}</ins> <span className="ac-caption">{text(error.explanation)}</span>
            </li>
          ))}
        </ul>
      )}
      {Array.isArray(grade.advice) && (grade.advice as unknown[]).length > 0 && (
        <ul className="ac-goethe-advice">
          {(grade.advice as unknown[]).map((advice, index) => (
            <li key={index}>{text(advice)}</li>
          ))}
        </ul>
      )}
      {help && <p className="ac-note">{help}</p>}
      {model ? (
        <details className="ac-goethe-model">
          <summary>{t("goethe.review.model")}</summary>
          <p lang="de">{model}</p>
        </details>
      ) : null}
      <MethodLink exam="goethe" skill={skill} task={text(row.teil)} type={text(grade.level)} />
    </GlassCard>
  );
}

export function GoetheResults({ id }: { id: string }) {
  const paths = useAcademyPaths();
  const { t, i18n } = useTranslation();
  const attempt = useAcademyAttemptsRetrieve(id, { query: { retry: false, refetchInterval: (q) => (JSON.stringify(q.state.data ?? "").includes('"pending":true') ? 5000 : false) } });
  const review = useAcademyAttemptsReviewRetrieve(id, { query: { retry: false } });
  const [skillTab, setSkillTab] = useState<GoetheSkill | "">("");
  const data = attempt.data?.status === 200 ? (attempt.data.data as unknown) : null;
  const results = useMemo(() => parseGoetheResults(isObject(data) ? data.results : null), [data]);
  const unavailable = unavailableSkills(data);
  const reviewData = review.data?.status === 200 ? (review.data.data as unknown as Record<string, unknown>) : null;
  const passages = useMemo(() => {
    const map = new Map<string, GoethePassage & { skill: string; audioUrl: string; transcript: ReturnType<typeof parseSpokenLines> }>();
    for (const raw of asList(reviewData?.passages)) {
      const passage = parseGoethePassage(raw);
      if (passage) map.set(passage.id, { ...passage, skill: text(raw.skill), audioUrl: text(raw.audio_url), transcript: parseSpokenLines(raw.transcript) });
    }
    return map;
  }, [reviewData]);
  if (attempt.isLoading) return <Skeleton style={{ height: 320 }} />;
  const level = results.level;
  const overall = results.overall;
  const modules = (["co", "ce", "ee", "eo"] as GoetheSkill[]).map((s) => results.modules[s]).filter((m): m is GoetheModuleResult => Boolean(m));
  const questions = asList(reviewData?.questions);
  const shownSkill = skillTab || (questions[0] ? (text(questions[0].skill) as GoetheSkill) : "co");
  const byPassage = new Map<string, Record<string, unknown>[]>();
  for (const q of questions.filter((item) => item.skill === shownSkill)) {
    const key = text(q.passage);
    byPassage.set(key, [...(byPassage.get(key) ?? []), q]);
  }

  return (
    <div className="ac-page ac-goethe ac-goethe-results">
      <PageHead eyebrow={`Goethe-Zertifikat ${level}`} title={t("goethe.results.title")} subtitle={<p className="ac-note" role="note">{t("goethe.estimateLong")}</p>} />
      {overall?.kind === "total" && (
        <GlassCard as="section" className={`ac-card ac-goethe-overall ac-goethe-overall--${overall.passed ? "ok" : "ko"}`} aria-labelledby="goethe-total">
          <CardTitle id="goethe-total" icon={<Award size={16} />} tone={overall.passed ? "success" : "warning"}>
            {overall.passed ? t("goethe.results.totalPassed") : t("goethe.results.totalFailed")}
          </CardTitle>
          <p className="ac-goethe-overall__score ao-num">
            {overall.total} <span className="ac-caption">/ {overall.max}</span>
          </p>
          {overall.rating && <p className="ac-goethe-result__rating">{ratingText(overall.rating, i18n.language)}</p>}
          <p className="ac-caption ao-num">
            {t("goethe.results.written", { n: formatPoints(overall.written, i18n.language), max: overall.writtenMax })} · {t("goethe.results.oral", { n: formatPoints(overall.oral, i18n.language), max: overall.oralMax })}
          </p>
          {level === "A2" && <p className="ac-footnote">{t("goethe.results.a2Rule")}</p>}
          {overall.writtenTooLow && <p className="ac-footnote ko">{t("goethe.results.a1Written")}</p>}
        </GlassCard>
      )}
      {overall?.kind === "modules" && (
        <p className="ac-note">{overall.allPassed ? t("goethe.results.allPassed") : t("goethe.results.modularNote", { count: overall.passedModules.length, total: overall.modules.length })}</p>
      )}
      <ul className="ac-goethe-results__grid">
        {modules.map((m) => (
          <ModuleCard key={m.skill} result={m} unavailable={unavailable.has(m.skill)} />
        ))}
      </ul>

      {questions.length > 0 && (
        <section className="ac-section" aria-labelledby="goethe-review">
          <h2 id="goethe-review" className="ac-title-2">
            {t("goethe.review.title")}
          </h2>
          <nav className="ac-goethe-tabs" aria-label={t("goethe.review.modules")}>
            {(["co", "ce"] as GoetheSkill[])
              .filter((s) => questions.some((q) => q.skill === s))
              .map((s) => (
                <button key={s} type="button" className={`ac-goethe-tab${shownSkill === s ? " ac-goethe-tab--on" : ""}`} onClick={() => setSkillTab(s)}>
                  {GOETHE_MODULES[s]}
                </button>
              ))}
          </nav>
          {[...byPassage.entries()].map(([passageId, rows]) => {
            const passage = passages.get(passageId) ?? null;
            return (
              <article key={passageId} className="ac-question__sheet ac-goethe-part">
                {passage && (
                  <>
                    <h3 className="ac-title-3">
                      {t("goethe.player.teil", { n: passage.teil })}
                      {passage.context ? ` · ${passage.context}` : ""}
                    </h3>
                    <ExamPictureView picture={passage.image} size="medium" />
                    {passage.example && <GoetheExampleCard example={passage.example} passage={passage} />}
                    {passage.skill === "co" && passage.audioUrl && <AudioReplay url={passage.audioUrl} label={t("goethe.review.listenAgain")} />}
                    {passage.skill === "co" && !passage.audioUrl && passage.lines.length > 0 && <SpeechReplay lines={passage.lines} lang="de" preferred="de-DE" label={t("goethe.review.listenAgain")} />}
                    {passage.skill === "co" && passage.transcript.length > 0 && (
                      <details className="ac-goethe-model">
                        <summary>{t("goethe.review.transcript")}</summary>
                        <Transcript lines={passage.transcript} lang="de" />
                      </details>
                    )}
                    {passage.skill === "ce" && <GoetheTexts passage={passage} />}
                    <GoethePool passage={passage} />
                  </>
                )}
                <ol className="ac-goethe-items">
                  {rows.map((q) => {
                    const question = {
                      id: text(q.id),
                      index: 0,
                      number: Number(q.number),
                      stem: text(q.stem),
                      options: Array.isArray(q.options) ? (q.options as unknown[]).map(text) : [],
                      format: text(q.format),
                      teil: Number(q.teil),
                      textId: text(q.text_id),
                      textIds: Array.isArray(q.text_ids) ? (q.text_ids as unknown[]).map(text) : [],
                      response: typeof q.choice === "number" ? q.choice : null,
                      image: parsePicture(q.image),
                      optionImages: parsePictures(q.option_images),
                    };
                    const explanation = text(i18n.language.startsWith("en") ? q.explanation_en || q.explanation : q.explanation || q.explanation_en);
                    return (
                      <div key={question.id}>
                        <GoetheItem question={question} passage={passage} value={question.response} answer={typeof q.answer === "number" ? q.answer : null} disabled />
                        <p className="ac-goethe-explanation">{explanation || t("goethe.review.noExplanation")}</p>
                        {explanation && <InMyLanguage text={explanation} source={i18n.language.startsWith("en") ? "en" : "fr"} examLanguage="de" kind="correction" />}
                      </div>
                    );
                  })}
                </ol>
                {passage && <MethodLink exam="goethe" skill={passage.skill} task={passage.teil} type={level} />}
              </article>
            );
          })}
        </section>
      )}

      {asList(reviewData?.productions).length > 0 && (
        <section className="ac-section" aria-labelledby="goethe-productions">
          <h2 id="goethe-productions" className="ac-title-2">
            {t("goethe.review.productions")}
          </h2>
          {asList(reviewData?.productions).map((row) => (
            <ProductionReview key={`${text(row.skill)}-${text(row.teil)}`} row={row} />
          ))}
        </section>
      )}
      <Link className={buttonClass("secondary")} to={paths.home}>
        {t("goethe.results.back")}
      </Link>
    </div>
  );
}
